import { translateLabel } from "@/i18n/messages";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AppShell, PageIntro } from "@/components/layout/AppShell";
import { operatorApi } from "@/lib/operatorApi";
import { toast } from "sonner";

const field = "w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm text-white outline-none placeholder:text-zinc-600 focus:border-[#CCFF00]/60";
const button = "rounded-lg bg-[#CCFF00] px-4 py-2 text-sm font-bold text-black disabled:opacity-40";
const subtle = "rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm font-semibold text-zinc-200 hover:bg-white/10 disabled:opacity-40";
const panel = "rounded-2xl border border-white/10 bg-[#18181b] p-4 md:p-5";
const err = (e) => e?.message || "Operator request failed";

const destinationFields = ["operationalStatus", "summary", "description", "website", "openingHours", "season", "parking"];

export default function Operator() {
  const [workspace, setWorkspace] = useState([]);
  const [destinationId, setDestinationId] = useState("");
  const [destination, setDestination] = useState(null);
  const [trails, setTrails] = useState([]);
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [changes, setChanges] = useState({});
  const [reason, setReason] = useState("");
  const [newTrail, setNewTrail] = useState({ name: "", difficulty: "UNKNOWN", length_km: "", trail_type: "", riding_style: "", surface: "", description: "", reason: "" });
  const [video, setVideo] = useState({ url: "", title: "" });
  const [report, setReport] = useState({ target_type: "REVIEW", target_id: "", category: "OTHER", details: "" });

  const destinations = useMemo(() => workspace.flatMap((entry) => entry.destinations || []), [workspace]);

  const loadWorkspace = useCallback(async () => {
    setLoading(true);
    try {
      const data = await operatorApi.workspace();
      setWorkspace(data || []);
      const first = data?.flatMap((entry) => entry.destinations || [])[0];
      if (first && !destinationId) setDestinationId(first.id);
    } catch (e) { toast.error(err(e)); }
    finally { setLoading(false); }
  }, [destinationId]);

  const loadDestination = useCallback(async () => {
    if (!destinationId) { setDestination(null); setTrails([]); setMedia([]); return; }
    try {
      const [d, t, m] = await Promise.all([
        operatorApi.destination(destinationId),
        operatorApi.trails(destinationId),
        operatorApi.media(destinationId),
      ]);
      setDestination(d); setTrails(t || []); setMedia(m || []); setChanges({});
    } catch (e) { toast.error(err(e)); }
  }, [destinationId]);

  useEffect(() => { loadWorkspace(); }, [loadWorkspace]);
  useEffect(() => { loadDestination(); }, [loadDestination]);

  const saveDestination = async (event) => {
    event.preventDefault();
    const payload = destinationFields
      .filter((key) => Object.prototype.hasOwnProperty.call(changes, key))
      .map((key) => ({ field: key, value: String(changes[key] ?? "") }));
    if (!payload.length) return toast.error(translateLabel("Change at least one field"));
    if (!reason.trim()) return toast.error(translateLabel("Reason is required"));
    setBusy(true);
    try {
      await operatorApi.updateDestination(destinationId, payload, reason.trim());
      toast.success(translateLabel("Official destination data updated"));
      setReason("");
      await loadDestination();
    } catch (e) { toast.error(err(e)); }
    finally { setBusy(false); }
  };

  const createTrail = async (event) => {
    event.preventDefault();
    if (!newTrail.name.trim() || !newTrail.reason.trim()) return toast.error(translateLabel("Trail name and reason are required"));
    setBusy(true);
    try {
      await operatorApi.createTrail({
        destination_id: destinationId,
        name: newTrail.name.trim(),
        difficulty: newTrail.difficulty,
        length_km: newTrail.length_km === "" ? null : Number(newTrail.length_km),
        trail_type: newTrail.trail_type.trim() || null,
        riding_style: newTrail.riding_style.trim() || null,
        surface: newTrail.surface.trim() || null,
        description: newTrail.description.trim() || null,
        reason: newTrail.reason.trim(),
      });
      toast.success(translateLabel("Official trail created"));
      setNewTrail({ name: "", difficulty: "UNKNOWN", length_km: "", trail_type: "", riding_style: "", surface: "", description: "", reason: "" });
      await loadDestination();
    } catch (e) { toast.error(err(e)); }
    finally { setBusy(false); }
  };

  const updateTrailStatus = async (trail, status) => {
    setBusy(true);
    try {
      await operatorApi.updateTrail({ destination_id: destinationId, trail_id: trail.id, changes: [{ field: "operationalStatus", value: status }], reason: "Operator status update" });
      toast.success(`${trail.name} status updated`);
      await loadDestination();
    } catch (e) { toast.error(err(e)); }
    finally { setBusy(false); }
  };

  const addVideo = async (event) => {
    event.preventDefault();
    if (!video.url.trim()) return;
    setBusy(true);
    try {
      await operatorApi.addVideo({ destination_id: destinationId, url: video.url.trim(), title: video.title.trim() || null });
      toast.success(translateLabel("Official video added"));
      setVideo({ url: "", title: "" });
      await loadDestination();
    } catch (e) { toast.error(err(e)); }
    finally { setBusy(false); }
  };

  const uploadPhoto = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const auth = await operatorApi.requestPhotoUpload({ destination_id: destinationId, filename: file.name, content_type: file.type, byte_size: file.size });
      const headers = Object.fromEntries((auth.headers || []).map((h) => [h.name, h.value]));
      const response = await fetch(auth.upload_url, { method: auth.method || "PUT", headers, body: file });
      if (!response.ok) throw new Error(`Photo upload failed (${response.status})`);
      await operatorApi.confirmPhotoUpload(auth.upload_id);
      toast.success(translateLabel("Official photo uploaded"));
      await loadDestination();
    } catch (e) { toast.error(err(e)); }
    finally { setBusy(false); }
  };

  const removeMedia = async (item) => {
    if (!window.confirm(translateLabel("Remove this operator-owned official media item?"))) return;
    setBusy(true);
    try { await operatorApi.removeMedia(destinationId, item.id); toast.success(translateLabel("Media removed")); await loadDestination(); }
    catch (e) { toast.error(err(e)); }
    finally { setBusy(false); }
  };

  const submitReport = async (event) => {
    event.preventDefault();
    if (!report.target_id.trim()) return toast.error(translateLabel("Target ID is required"));
    setBusy(true);
    try {
      await operatorApi.report({ ...report, target_id: report.target_id.trim(), details: report.details.trim() || null });
      toast.success(translateLabel("Report sent to BPF Admin review"));
      setReport((v) => ({ ...v, target_id: "", details: "" }));
    } catch (e) { toast.error(err(e)); }
    finally { setBusy(false); }
  };

  return (
    <AppShell size="wide" tone="workspace" contentClassName="space-y-5">
        <PageIntro eyebrow="Verified operator" title={translateLabel("Operator panel")} description={translateLabel("Manage only official data for destinations assigned to your organization.")} className="mb-1" />

        {loading ? <div className={panel}>{translateLabel("Loading operator workspace…")}</div> : destinations.length === 0 ? (
          <div className={panel} data-testid="operator-empty"><h2 className="font-display text-xl font-bold">{translateLabel("No managed destinations")}</h2><p className="mt-2 text-sm text-zinc-400">{translateLabel("Your account has no active destination access. Claim approval or Admin access assignment is required.")}</p></div>
        ) : <>
          <section className={panel}>
            <label className="mb-2 block text-xs font-bold uppercase tracking-wide text-zinc-500">{translateLabel("Managed destination")}</label>
            <select className={field} value={destinationId} onChange={(e) => setDestinationId(e.target.value)}>
              {workspace.flatMap((entry) => (entry.destinations || []).map((d) => <option key={d.id} value={d.id}>{entry.organization.name} — {d.name}</option>))}
            </select>
            {destination && <div className="mt-3 flex flex-wrap gap-2 text-xs text-zinc-400"><span>{destination.organization.name}</span><span>•</span><span>{destination.organization.verification_status}</span><span>•</span><span>{destination.access.status}</span><span>•</span><span>{destination.publication_state}</span></div>}
          </section>

          {destination && <div className="grid gap-5 xl:grid-cols-2">
            <form className={panel} onSubmit={saveDestination} data-testid="operator-destination-editor">
              <h2 className="font-display text-xl font-bold">{translateLabel("Official destination details")}</h2>
              <div className="mt-4 space-y-2">
                <select className={field} value={changes.operationalStatus ?? destination.operational_status} onChange={(e) => setChanges((v) => ({ ...v, operationalStatus: e.target.value }))}><option>OPEN</option><option>CLOSED</option><option>UNKNOWN</option></select>
                {[['summary','Summary'],['description','Description'],['website','Website'],['openingHours','Opening hours'],['season','Season'],['parking','Parking']].map(([key,label]) => key === 'description' ? <textarea key={key} className={`${field} min-h-24`} value={changes[key] ?? destination[key === 'openingHours' ? 'opening_hours' : key] ?? ''} onChange={(e) => setChanges((v) => ({ ...v, [key]: e.target.value }))} placeholder={label} /> : <input key={key} className={field} value={changes[key] ?? destination[key === 'openingHours' ? 'opening_hours' : key] ?? ''} onChange={(e) => setChanges((v) => ({ ...v, [key]: e.target.value }))} placeholder={label} />)}
                <input className={field} value={reason} onChange={(e) => setReason(e.target.value)} placeholder={translateLabel("Reason for update")} />
                <button className={button} disabled={busy}>{translateLabel("Save official data")}</button>
              </div>
            </form>

            <form className={panel} onSubmit={createTrail}>
              <h2 className="font-display text-xl font-bold">{translateLabel("Add official trail")}</h2>
              <div className="mt-4 space-y-2"><input className={field} value={newTrail.name} onChange={(e) => setNewTrail({ ...newTrail, name: e.target.value })} placeholder={translateLabel("Trail name")} /><select className={field} value={newTrail.difficulty} onChange={(e) => setNewTrail({ ...newTrail, difficulty: e.target.value })}><option>UNKNOWN</option><option>EASY</option><option>INTERMEDIATE</option><option>DIFFICULT</option><option>EXPERT</option></select><input className={field} value={newTrail.length_km} onChange={(e) => setNewTrail({ ...newTrail, length_km: e.target.value })} placeholder={translateLabel("Length km")} /><input className={field} value={newTrail.trail_type} onChange={(e) => setNewTrail({ ...newTrail, trail_type: e.target.value })} placeholder={translateLabel("Trail type")} /><input className={field} value={newTrail.riding_style} onChange={(e) => setNewTrail({ ...newTrail, riding_style: e.target.value })} placeholder={translateLabel("Riding style")} /><input className={field} value={newTrail.surface} onChange={(e) => setNewTrail({ ...newTrail, surface: e.target.value })} placeholder={translateLabel("Surface")} /><textarea className={field} value={newTrail.description} onChange={(e) => setNewTrail({ ...newTrail, description: e.target.value })} placeholder={translateLabel("Description")} /><input className={field} value={newTrail.reason} onChange={(e) => setNewTrail({ ...newTrail, reason: e.target.value })} placeholder={translateLabel("Reason")} /><button className={button} disabled={busy}>{translateLabel("Create trail")}</button></div>
            </form>
          </div>}

          <section className={panel}>
            <h2 className="font-display text-xl font-bold">{translateLabel("Trails")}</h2>
            <div className="mt-4 grid gap-3 md:grid-cols-2">{trails.map((trail) => <div key={trail.id} className="rounded-xl border border-white/10 bg-black/20 p-3"><div className="font-semibold">{trail.name}</div><div className="mt-1 text-xs text-zinc-500">{trail.difficulty} · {trail.length_km ?? '—'} km · {trail.operational_status}</div><select className={`${field} mt-3`} value={trail.operational_status} disabled={busy} onChange={(e) => updateTrailStatus(trail, e.target.value)}><option>OPEN</option><option>CLOSED</option><option>UNKNOWN</option></select></div>)}</div>
          </section>

          <div className="grid gap-5 xl:grid-cols-2">
            <section className={panel}>
              <h2 className="font-display text-xl font-bold">{translateLabel("Official media")}</h2>
              <form className="mt-4 space-y-2" onSubmit={addVideo}><input className={field} value={video.url} onChange={(e) => setVideo({ ...video, url: e.target.value })} placeholder={translateLabel("YouTube URL")} /><input className={field} value={video.title} onChange={(e) => setVideo({ ...video, title: e.target.value })} placeholder={translateLabel("Title (optional)")} /><div className="flex flex-wrap gap-2"><button className={button} disabled={busy}>{translateLabel("Add video")}</button><label className={`${subtle} cursor-pointer`}>{translateLabel("Upload photo")}<input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" disabled={busy} onChange={uploadPhoto} /></label></div></form>
              <div className="mt-4 space-y-2">{media.map((item) => <div key={item.id} className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-black/20 p-3"><div className="min-w-0"><div className="truncate text-sm font-semibold">{item.title || item.media_type}</div><div className="truncate text-xs text-zinc-500">{item.source_type} · {item.publication_status}</div></div><button className={subtle} disabled={busy} onClick={() => removeMedia(item)}>{translateLabel("Remove")}</button></div>)}</div>
            </section>

            <form className={panel} onSubmit={submitReport}>
              <h2 className="font-display text-xl font-bold">{translateLabel("Report community content")}</h2><p className="mt-1 text-sm text-zinc-500">{translateLabel("Reports go to BPF Admin review. This does not hide or remove community content.")}</p>
              <div className="mt-4 space-y-2"><select className={field} value={report.target_type} onChange={(e) => setReport({ ...report, target_type: e.target.value })}><option>REVIEW</option><option>MEDIA</option></select><input className={field} value={report.target_id} onChange={(e) => setReport({ ...report, target_id: e.target.value })} placeholder={translateLabel("Community review/media ID")} /><select className={field} value={report.category} onChange={(e) => setReport({ ...report, category: e.target.value })}><option>INCORRECT_INFO</option><option>SPAM</option><option>ABUSE</option><option>INAPPROPRIATE</option><option>COPYRIGHT</option><option>SAFETY</option><option>OTHER</option></select><textarea className={`${field} min-h-24`} value={report.details} onChange={(e) => setReport({ ...report, details: e.target.value })} placeholder={translateLabel("Details")} /><button className={button} disabled={busy}>{translateLabel("Send report")}</button></div>
            </form>
          </div>
        </>}
    </AppShell>
  );
}
